import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { TREE_VIEW_ONBOARDING_SPOTLIGHT_DISMISSED } from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadTreeViewOnboardingSpotlightDismissed =
	(): Action<State, Props> =>
	({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return Promise.resolve();
		}

		// If a load is already in flight, join it instead of firing a duplicate request
		const existing = getState().treeViewOnboarding.loadingPromise;
		if (existing !== undefined) {
			return existing;
		}

		// User property is already loaded — no need to fetch
		if (typeof getState().treeViewOnboarding.spotlightDismissed === 'boolean') {
			return Promise.resolve();
		}

		const promise = (async () => {
			await maybeWaitForPremiumModal(getState);

			try {
				const spotlightDismissed = await getUserProperty<boolean>(
					accountId,
					TREE_VIEW_ONBOARDING_SPOTLIGHT_DISMISSED,
				);

				const state = getState();

				setState({
					...state,
					treeViewOnboarding: {
						...state.treeViewOnboarding,
						spotlightDismissed,
						loadingPromise: undefined,
					},
				});
			} catch (error) {
				// Expected error, the property is not set yet
				if (error instanceof FetchError && error.statusCode === 404) {
					const state = getState();

					setState({
						...state,
						treeViewOnboarding: {
							...state.treeViewOnboarding,
							spotlightDismissed: false,
							loadingPromise: undefined,
						},
					});

					return;
				}

				const state = getState();
				setState({
					...state,
					treeViewOnboarding: {
						...state.treeViewOnboarding,
						loadingPromise: undefined,
					},
				});

				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.onboarding.tree-view-onboarding-spotlight',
					`Failed to get user property with key: ${TREE_VIEW_ONBOARDING_SPOTLIGHT_DISMISSED}`,
					error instanceof Error ? error : new Error(String(error)),
				);
			}
		})();

		const state = getState();
		setState({
			...state,
			treeViewOnboarding: {
				...state.treeViewOnboarding,
				loadingPromise: promise,
			},
		});

		return promise;
	};

export const setTreeViewOnboardingSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			treeViewOnboarding: {
				...state.treeViewOnboarding,
				spotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, TREE_VIEW_ONBOARDING_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			// Roll back the optimistic update so the spotlight can still show this session
			const currentState = getState();
			setState({
				...currentState,
				treeViewOnboarding: {
					...currentState.treeViewOnboarding,
					spotlightDismissed: false,
				},
			});

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.tree-view-onboarding-spotlight',
				`Failed to set user property with key: ${TREE_VIEW_ONBOARDING_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};
